import Decimal from "decimal.js";
import type { CostRow, Custom } from "./calculator";
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export type ProductionHelper = {
  enabled: boolean;
  source: "item" | "material" | "manual";
  materialRowId?: string | null;
  quantity?: string | null;
  unit: string;
  rate: string;
  period: "hour" | "day";
};
export type TravelHelper = { enabled: boolean; roundTripHours: string; visits?: string | null };
export type HaulingHelper = {
  quantityMode?: "product_tons" | "manual";
  haulQuantity?: string | null;
  haulUnit?: "Tons" | "Yards";
  planningEnabled?: boolean;
  materialRowId: string;
  allocationPercent: string;
  capacity: string;
  capacityUnit: "Tons" | "Yards";
  roundTripHours: string;
  loadHours: string;
  unloadHours: string;
  planningHours?: string | null;
  maxTripsPerDay?: string | null;
  fullLoads: boolean;
};
export type Helpers = {
  hoursPerDay: string;
  production?: ProductionHelper | null;
  travel?: TravelHelper | null;
};
const n = (v: string | undefined | null, positive = false) => {
  if (!/^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(v ?? ""))
    throw Error("Enter a nonnegative calculation input.");
  const x = new D(v!);
  if (positive && x.lte(0)) throw Error("Enter a positive calculation input.");
  return x;
};
const unit = (u: string): [string, string] => {
  const s = u.trim().toLowerCase();
  if (["sq ft", "sq_ft", "square feet", "square foot"].includes(s)) return ["area", "1"];
  if (["sq yd", "sq yds", "sq_yd", "square yards", "square yard"].includes(s)) return ["area", "9"];
  if (["lin ft", "lin_ft", "linear feet"].includes(s)) return ["length", "1"];
  if (["ton", "tons", "us_ton"].includes(s)) return ["weight", "2000"];
  if (["pound", "pounds", "lb", "lbs"].includes(s)) return ["weight", "1"];
  if (["yards", "cubic yd"].includes(s)) return ["volume", "1"];
  return [s, "1"];
};
const convert = (q: Decimal, from: string, to: string) => {
  const [a, x] = unit(from),
    [b, y] = unit(to);
  if (a !== b) throw Error("Choose compatible measurement units.");
  return q.mul(x).div(y);
};
/** Lock production rates to their quantity source while preserving equivalent saved rates. */
export function alignProductionUnit(item: Custom, production: ProductionHelper): ProductionHelper {
  const next =
    production.source === "manual"
      ? { ...production, source: "item" as const, rate: "", enabled: false }
      : { ...production };
  const target =
    next.source === "material"
      ? item.rows.find((row) => row.rowId === next.materialRowId)?.unit ?? ""
      : item.unit;
  if (next.unit !== target) {
    try {
      if (next.rate)
        next.rate = convert(n(next.rate), next.unit, target).toDecimalPlaces(8).toString();
    } catch {
      next.rate = "";
      next.enabled = false;
    }
    next.unit = target;
  }
  return next;
}

export function resolveHelpers(item: Custom, materialQuantity: (r: CostRow) => Decimal) {
  if (item.calculationVersion !== 2)
    return {
      rows: item.rows,
      hours: null as Decimal | null,
      travel: new D(0),
      plans: {} as Record<string, string>,
    };
  const ids = new Map<string, CostRow>();
  item.rows.forEach((r) => {
    if (!r.rowId || ids.has(r.rowId)) throw Error("Cost rows need unique identifiers.");
    ids.set(r.rowId, r);
  });
  const material = (id: string, u: string) => {
    const r = ids.get(id);
    if (
      !r ||
      r.category !== "material" ||
      !["manual", "material"].includes(r.quantitySource ?? "manual")
    )
      throw Error("Select an existing material source.");
    return convert(materialQuantity(r), r.unit, u);
  };
  const h = item.helpers,
    p = h?.production,
    t = h?.travel,
    day = n(h?.hoursPerDay ?? "8", true);
  let hours: Decimal | null = null,
    travel = new D(0);
  if (p?.enabled) {
    const q =
      p.source === "material"
        ? material(p.materialRowId ?? "", p.unit)
        : p.source === "manual"
        ? n(p.quantity)
        : convert(n(item.quantity), item.unit, p.unit);
    hours = q.div(n(p.rate, true)).mul(p.period === "day" ? day : 1);
  }
  if (t?.enabled) {
    const visits = t.visits != null ? n(t.visits) : hours?.div(day).ceil();
    if (visits == null) throw Error("Enter travel visits or enable production duration.");
    travel = visits.mul(n(t.roundTripHours));
  }
  const plans: Record<string, string> = {};
  const allocations = new Map<string, Decimal>();
  const rows = item.rows.map((original) => {
    const r = { ...original };
    const source = r.quantitySource ?? "manual";
    if (["manual", "material"].includes(source)) return r;
    if ((r.calculation ?? "manual") !== "manual")
      throw Error("Each quantity may have only one calculation source.");
    let qty = new D(0);
    if (source === "trucking") {
      if (r.category !== "trucking" || !r.hauling) throw Error("Configure the hauling planner.");
      const c = r.hauling;
      let amount: Decimal;
      if (c.quantityMode) {
        amount = convert(
          haulingQuantity(item, c, materialQuantity),
          c.haulUnit ?? "Tons",
          c.capacityUnit
        );
      } else {
        const share = n(c.allocationPercent, true),
          allocated = (allocations.get(c.materialRowId) ?? new D(0)).plus(share);
        allocations.set(c.materialRowId, allocated);
        if (allocated.gt(100))
          throw Error("Trucking allocations exceed 100% of the selected material.");
        amount = material(c.materialRowId, c.capacityUnit).mul(share).div(100);
      }
      const cap = n(c.capacity, true);
      const loads = amount.div(cap).ceil();
      plans[r.rowId!] = loads.toString();
      let trucks = new D(0),
        cycle = new D(0);
      if (r.truckingUnit === "Hours" || c.planningEnabled !== false) {
        cycle = n(c.roundTripHours).plus(n(c.loadHours)).plus(n(c.unloadHours));
        if (cycle.lte(0)) throw Error("Enter a positive calculation input.");
        const duration =
          c.planningHours != null
            ? n(c.planningHours, true)
            : hours ?? (c.quantityMode ? day : null);
        if (!duration || duration.lte(0))
          throw Error("Enter a positive trucking planning duration.");
        // Adapter mirrors calculateTruckQuantity, retaining its one-decimal staffing rule.
        const production = Decimal.min(
          amount.div(duration).div(cap).mul(cycle).toDecimalPlaces(1),
          loads
        );
        let count = production;
        if (c.maxTripsPerDay != null) {
          const maxTrips = n(c.maxTripsPerDay, true);
          if (!maxTrips.isInteger()) throw Error("Maximum trips per day must be a whole number.");
          let limit = loads.div(maxTrips).toDecimalPlaces(1);
          if (duration.div(day).gt(1)) limit = limit.div(duration.div(day));
          count = Decimal.max(count, limit);
        }
        trucks = amount.isZero()
          ? new D(0)
          : cap.gt(amount)
          ? new D(1)
          : Decimal.max(1, count.ceil());
        r.trucksNeeded = trucks.toString();
      }
      if (c.fullLoads) amount = loads.mul(cap);
      qty =
        r.truckingUnit === "Hours"
          ? trucks.isZero()
            ? new D(0)
            : loads.mul(cycle).div(trucks)
          : r.truckingUnit === "Loads"
          ? loads
          : convert(amount, c.capacityUnit, r.truckingUnit ?? "Hours");
    } else {
      if (
        !["duration", "travel", "duration_travel"].includes(source) ||
        !["labor", "equipment"].includes(r.category) ||
        r.unit.toLowerCase() !== "hours"
      )
        throw Error("Duration links require hourly labor or equipment.");
      if (source.includes("duration")) {
        if (!hours) throw Error("Enable production duration for linked rows.");
        qty = hours;
      }
      if (source.includes("travel")) {
        if (!t?.enabled) throw Error("Enable travel for linked rows.");
        qty = qty.plus(travel);
      }
    }
    r.quantity =
      r.quantityOverride != null
        ? n(r.quantityOverride).toString()
        : qty.toDecimalPlaces(r.category === "labor" ? 2 : 8).toString();
    if (r.category === "labor") r.quantity = n(r.quantity).toDecimalPlaces(2).toString();
    r.calculation = "manual";
    return r;
  });
  return { rows, hours, travel, plans };
}
export function ensureRowIds(item: Custom): Custom {
  return item.calculationVersion === 2
    ? { ...item, rows: item.rows.map((r) => ({ ...r, rowId: r.rowId ?? crypto.randomUUID() })) }
    : item;
}

/** Freeze only quantities that depend on the deleted source; unrelated links remain active. */
export function removeSourceRow(
  item: Custom,
  index: number,
  materialQuantity: (r: CostRow) => Decimal
): Custom {
  const resolved = resolveHelpers(item, materialQuantity);
  const id = item.rows[index].rowId;
  const durationLost =
    !!id &&
    item.helpers?.production?.enabled &&
    item.helpers.production.source === "material" &&
    item.helpers.production.materialRowId === id;
  const travelLost =
    durationLost && item.helpers?.travel?.enabled && item.helpers.travel.visits == null;
  return {
    ...item,
    helpers: item.helpers
      ? {
          ...item.helpers,
          production: durationLost
            ? { ...item.helpers.production!, enabled: false, materialRowId: null }
            : item.helpers.production,
          travel: travelLost ? { ...item.helpers.travel!, enabled: false } : item.helpers.travel,
        }
      : null,
    rows: item.rows
      .map((r, i) => {
        const source = r.quantitySource ?? "manual";
        const affected =
          (source === "trucking" &&
            (r.hauling?.materialRowId === id ||
              (durationLost && r.hauling?.planningHours == null))) ||
          (durationLost && source.includes("duration")) ||
          (travelLost && source.includes("travel"));
        return affected
          ? {
              ...r,
              quantity: resolved.rows[i].quantity,
              trucksNeeded: resolved.rows[i].trucksNeeded,
              quantitySource: "manual" as const,
              quantityOverride: null,
            }
          : r;
      })
      .filter((_, i) => i !== index),
  };
}

/** New hourly labor follows the active settings already used by this product's labor. */
export function inheritLaborTime(item: Custom, row: CostRow): CostRow {
  if (row.category !== "labor" || row.unit !== "Hours") return row;
  const crews = item.rows.filter((r) => r.category === "labor" && r.unit === "Hours");
  const duration =
    !!item.helpers?.production?.enabled &&
    crews.some((r) => r.quantitySource?.includes("duration"));
  const travel =
    !!item.helpers?.travel?.enabled && crews.some((r) => r.quantitySource?.includes("travel"));
  const source =
    duration && travel ? "duration_travel" : duration ? "duration" : travel ? "travel" : "manual";
  return source === "manual" ? row : { ...row, quantitySource: source };
}

/** A crew-specific drawer configures that crew without a second row-selection step. */
export function linkSelectedCrew(item: Custom, index?: number): Custom {
  if (
    index === undefined ||
    item.rows[index]?.category !== "labor" ||
    item.rows[index]?.unit !== "Hours"
  )
    return item;
  const duration = !!item.helpers?.production?.enabled;
  const travel = !!item.helpers?.travel?.enabled;
  const source: CostRow["quantitySource"] =
    duration && travel ? "duration_travel" : duration ? "duration" : travel ? "travel" : "manual";
  return {
    ...item,
    rows: item.rows.map((row, i) => (i === index ? { ...row, quantitySource: source } : row)),
  };
}

export function haulingQuantity(
  item: Custom,
  config: HaulingHelper,
  materialQuantity: (row: CostRow) => Decimal
): Decimal {
  if (config.quantityMode === "manual") return n(config.haulQuantity);
  const materials = item.rows.filter(
    (row) =>
      row.category === "material" &&
      ["ton", "tons", "us_ton"].includes(row.unit.trim().toLowerCase())
  );
  if (!materials.length)
    throw Error("Enter the total quantity to haul; this product has no ton-based materials.");
  if (config.haulUnit && config.haulUnit !== "Tons")
    throw Error("Enter a separate quantity for cubic yards.");
  return materials.reduce((total, row) => total.plus(materialQuantity(row)), new D(0));
}

export function simplifyHauling(
  item: Custom,
  row: CostRow,
  materialQuantity: (row: CostRow) => Decimal
): HaulingHelper {
  const old = row.hauling;
  if (old?.quantityMode) return old;
  const base: HaulingHelper = {
    materialRowId: "",
    allocationPercent: "100",
    capacity: String(row.truckCapacity ?? ""),
    capacityUnit: "Tons",
    roundTripHours: "1",
    loadHours: "0",
    unloadHours: "0",
    fullLoads: false,
    ...old,
    planningEnabled: true,
  };
  if (old) {
    const material = item.rows.find((r) => r.rowId === old.materialRowId);
    return {
      ...base,
      quantityMode: "manual",
      materialRowId: "",
      allocationPercent: "100",
      haulUnit: old.capacityUnit,
      haulQuantity: material
        ? convert(materialQuantity(material), material.unit, old.capacityUnit)
            .mul(n(old.allocationPercent))
            .div(100)
            .toString()
        : "",
      roundTripHours: n(old.roundTripHours)
        .plus(n(old.loadHours))
        .plus(n(old.unloadHours))
        .toString(),
      loadHours: "0",
      unloadHours: "0",
      maxTripsPerDay: null,
    };
  }
  return { ...base, quantityMode: "product_tons", haulUnit: "Tons" };
}
